import { useEffect, useRef } from 'react';

import axios from 'axios';

import { EPortalRumEvents, logPortalRumError } from '@/lib/coralogix';
import { EHttpErrorCodes, ELocalStorageKeys } from '@/lib/enums';
import { loginStateService } from '@/state/login.state.service';
import { webStorageUtil } from '@/state/webStorage.state.service';

import { isStaleSessionAuthFailure } from './sessionAuthFailure.utils';

export const useSessionGuard = (logout: () => void) => {
  const isLoggingOutRef = useRef(false);
  const logoutRef = useRef(logout);
  logoutRef.current = logout;

  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (
          (error?.response?.status === EHttpErrorCodes.FORBIDDEN ||
            error?.response?.status === EHttpErrorCodes.UNAUTHORIZED) &&
          loginStateService.isLoggedIn.get() &&
          !isLoggingOutRef.current &&
          !isStaleSessionAuthFailure(error)
        ) {
          isLoggingOutRef.current = true;
          // Logging out wipes the player, so the identity is read first. This
          // ends the session without any failure being surfaced to the player
          // or the publisher — from the outside the player simply stops.
          logPortalRumError(EPortalRumEvents.PORTAL_SESSION_REJECTED, {
            playerId: webStorageUtil.get(ELocalStorageKeys.PLAYER_DATA)?.playerId,
            status: error?.response?.status,
            requestPath: readRequestPath(error?.config?.url),
          });
          logoutRef.current();
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
  }, []);
};

// Path only: a request URL can carry tokens in its query string.
const readRequestPath = (url?: string): string | undefined => {
  if (!url) return undefined;
  try {
    return new URL(url, window.location.origin).pathname;
  } catch {
    return undefined;
  }
};
